import { Icon } from '../basic';
import React from 'react';
import type { TaskDueDateInfo } from '../../util';
import {
  getTaskStatusText,
  isTaskStatusCompleted,
  useTaskDueDateInfo
} from '../../util';
import type { ClassNameProp } from '../common-types';
import type { icModel } from 'generated-ts-common';
import { intl } from '../../lib';

type Task = icModel.saf.infrastructure.model.appointments.Task;

/**
 * Props for a {@link TaskSubTitle} component.
 */
export interface TaskSubTitleProps extends ClassNameProp {
  task: Task;
  showIcon: boolean;
}

/**
 * Component for displaying a task subtitle in task detail / task list.
 *
 * @param properties a {@link TaskSubTitleProps} object
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export function TaskSubTitle({
  task,
  showIcon,
  className = '',
  ...props
}: Readonly<TaskSubTitleProps>): React.JSX.Element {
  const dueDateInfo = useTaskDueDateInfo(task);
  return (
    <div
      {...props}
      className={`ehs-task-subtitle-container s-font font-16 ${className}`.trim()}
    >
      {dueDateInfo
        ? dueDateInfo.text
        : intl('common.task.status') + ' ' + getTaskStatusText(task)}
      {showIcon && getIcon(dueDateInfo, task)}
    </div>
  );
}

function getIcon(dueDateInfo: TaskDueDateInfo, task: Task): React.JSX.Element {
  if (dueDateInfo && dueDateInfo.isWarning) {
    return (
      <Icon
        iconName={'materialicon-error'}
        className={
          'ehs-task-subtitle-warning-icon ' +
          (dueDateInfo.isPastDue ? 'task-past-due' : 'task-due')
        }
        aria-label={undefined}
      />
    );
  }
  if (!dueDateInfo && isTaskStatusCompleted(task?.status)) {
    return <Icon aria-label={undefined} iconName="shui_completed-task" />;
  }
  return <></>;
}
